import {
  apiRequest,
  API_BASE_URL,
  getAuthToken,
  getWithoutQueryString,
  appendUserIdQueryParam,
} from "./apiClient";
import { userTimezoneHeaders } from "../utils/userTimezoneHeader";
import { tokenManager } from "../utils/tokenManager";


/**
 * IoT Device API endpoints - Commented Out
 */
// export const iotDeviceAPI = {
//   /**
//    * Get all IoT devices with pagination and filters
//    */
//   getAll: async (params = {}) => {
//     const queryString = new URLSearchParams(params).toString();
//     const response = await apiRequest(`/iot-devices?${queryString}`);
//     return response.data;
//   },

//   /**
//    * Get IoT device by ID
//    */
//   getById: async (id) => {
//     const response = await apiRequest(`/iot-devices/${id}`);
//     return response.data;
//   },

//   /**
//    * Get IoT device by UID
//    */
//   getByUID: async (uid) => {
//     const response = await apiRequest(`/iot-devices/uid/${uid}`);
//     return response.data;
//   },

//   /**
//    * Create new IoT device
//    */
//   create: async (deviceData) => {
//     const response = await apiRequest('/iot-devices', {
//       method: 'POST',
//       body: JSON.stringify(deviceData),
//     });
//     return response.data;
//   },

//   /**
//    * Update IoT device
//    */
//   update: async (id, deviceData) => {
//     const response = await apiRequest(`/iot-devices/${id}`, {
//       method: 'PUT',
//       body: JSON.stringify(deviceData),
//     });
//     return response.data;
//   },

//   /**
//    * Delete IoT device
//    */
//   delete: async (id) => {
//     return apiRequest(`/iot-devices/${id}`, {
//       method: 'DELETE',
//     });
//   },

//   /**
//    * Update IoT device health status
//    */
//   updateHealth: async (id, healthStatus) => {
//     const response = await apiRequest(`/iot-devices/${id}/health`, {
//       method: 'PATCH',
//       body: JSON.stringify({ health_status: healthStatus }),
//     });
//     return response.data;
//   },

//   /**
//    * Update IoT device status
//    */
//   updateStatus: async (id, status) => {
//     const response = await apiRequest(`/iot-devices/${id}/status`, {
//       method: 'PATCH',
//       body: JSON.stringify({ status }),
//     });
//     return response.data;
//   },

//   /**
//    * Bulk update device status
//    */
//   bulkUpdateStatus: async (deviceIds, status) => {
//     const response = await apiRequest('/iot-devices/bulk/status', {
//       method: 'PATCH',
//       body: JSON.stringify({ device_ids: deviceIds, status }),
//     });
//     return response.data;
//   },

//   /**
//    * Get IoT device statistics
//    */
//   getStats: async () => {
//     const response = await apiRequest('/iot-devices/stats');
//     return response.data;
//   },
// };
/**
 * Dashboard API endpoints
 */
export const dashboardAPI = {
  /**
   * Get dashboard statistics
   * @param {Object} params - Query parameters
   * @param {string} params.period - Time period (e.g., 'last_7_days', 'last_30_days')
   * @param {number} params.use_case_id - Use case ID (optional)
   * @param {number} params.source_id - Source ID (optional)
   * @returns {Promise<Object>} Dashboard statistics
   */
  getStatistics: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.period) {
      queryParams.append("period", params.period);
    }
    if (params.use_case_id) {
      queryParams.append("use_case_id", params.use_case_id);
    }
    if (params.source_id) {
      queryParams.append("source_id", params.source_id);
    }

    const queryString = queryParams.toString();
    const endpoint = `/alerts/dashboard/statistics${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },
};


/**
 * Alert History API endpoints
 */
export const alertsHistoryAPI = {
  /**
   * Get alert history with filters
   * @param {Object} params - Query parameters
   * @param {string} params.search - Search query for filtering alerts (optional)
   * @param {string} params.alert_name - Filter by alert name (partial match, case-insensitive) (optional, deprecated - use search)
   * @param {number} params.limit - Number of results to return (deprecated, use per_page)
   * @param {number} params.offset - Number of results to skip for pagination (deprecated, use page)
   * @param {number} params.page - Page number for pagination (optional, default: 1)
   * @param {number} params.page_size - Page size (1–200); preferred for GET /alerts/history
   * @param {number} params.per_page - Alias for page_size (optional)
   * @param {string} params.period - Time period filter (optional)
   * @param {string} params.start_date - Start￼ date in YYYY-MM-DD format (optional)
   * @param {string} params.end_date - End date in YYYY-MM-DD format (optional)
   * @param {string|number|Array} params.source_id - Filter by source ID(s) - can be single value, comma-separated string, or array (optional)
   * @param {string|Array} params.severity - Filter by severity level(s): low, medium, high - can be single value, comma-separated string, or array (optional)
   * @param {string|number|Array} params.rule_id - Filter by rule ID(s) - can be single value, comma-separated string, or array (optional)
   * @param {string} params.media_type - Filter by media type: photo or video (optional)
   * @param {string} [params.sort_by] - Sort field: `created_at` (default) or `severity`
   * @param {string} [params.sort_order] - `asc` or `desc` (default `desc`)
   * @returns {Promise<Object>} Alert history data
   */
  getHistory: async (params = {}) => {
    const queryParams = new URLSearchParams();
    const appendCsvQueryParam = (key, value) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value)) {
        const vals = value
          .map((v) => String(v ?? "").trim())
          .filter(Boolean);
        if (vals.length > 0) queryParams.append(key, vals.join(","));
        return;
      }
      const s = String(value).trim();
      if (s) queryParams.append(key, s);
    };

    // Use search parameter (preferred) or fallback to alert_name for backward compatibility
    if (params.search) {
      queryParams.append("search", params.search);
    } else if (params.alert_name) {
      queryParams.append("alert_name", params.alert_name);
    }
    // Support both limit and per_page - API accepts limit
    const pageSizeForQuery =
      params.page_size ?? params.per_page ?? params.limit ?? null;
    if (pageSizeForQuery != null && String(pageSizeForQuery).trim() !== "") {
      queryParams.append("page_size", String(pageSizeForQuery));
    }
    if (params.page) {
      queryParams.append("page", params.page);
    }
    // Support legacy offset for backward compatibility
    if (params.offset && !params.page) {
      const ps = Number(pageSizeForQuery) || 20;
      const page = Math.floor(params.offset / ps) + 1;
      queryParams.append("page", page);
    }
    if (params.period) {
      queryParams.append("period", params.period);
    }
    if (params.start_date) {
      queryParams.append("start_date", params.start_date);
    }
    if (params.end_date) {
      queryParams.append("end_date", params.end_date);
    }
    appendCsvQueryParam("source_id", params.source_id);
    appendCsvQueryParam("source_name", params.source_name);
    appendCsvQueryParam("rule_id", params.rule_id);
    appendCsvQueryParam("severity", params.severity);
    appendCsvQueryParam("status", params.status);
    appendCsvQueryParam("notification_status", params.notification_status);
    appendCsvQueryParam("notify_by", params.notify_by);
    appendCsvQueryParam("alert_type", params.alert_type);
    appendCsvQueryParam("roi_id", params.roi_id);

    if (params.media_type) {
      queryParams.append("media_type", params.media_type);
    }
    const sortBy =
      params.sort_by != null && String(params.sort_by).trim() !== ""
        ? String(params.sort_by).trim()
        : "created_at";
    queryParams.append("sort_by", sortBy);
    const sortOrderRaw = String(params.sort_order ?? "desc")
      .trim()
      .toLowerCase();
    queryParams.append(
      "sort_order",
      sortOrderRaw === "asc" ? "asc" : "desc",
    );

    const queryString = queryParams.toString();
    const endpoint = `/alerts/history${queryString ? `?${queryString}` : ""}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * PATCH single alert workspace status (query param), e.g. acknowledged / closed / active.
   * Matches backend: PATCH /alert/{id}?status=...
   * Uses fetch directly so empty JSON bodies do not break parsing.
   */
  patchAlertStatus: async (alertId, status) => {
    const id = String(alertId ?? "").trim();
    if (!id) {
      throw new Error("Alert id is required");
    }
    const token = getAuthToken();
    const query = new URLSearchParams({
      status: String(status ?? "").trim().toLowerCase(),
    });
    const endpoint = `/alert/${encodeURIComponent(id)}?${query}`;
    const fullUrl = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(fullUrl, {
      method: "PATCH",
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
        ...userTimezoneHeaders(),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.loggedIn === false) {
        tokenManager.clearAuth();
        window.location.href = "/login";
        return null;
      }
      const error = new Error(
        errorData.message || `HTTP error! status: ${response.status}`,
      );
      error.status = response.status;
      error.data = errorData;
      error.errors = errorData.errors || [];
      throw error;
    }

    const text = await response.text();
    if (!text.trim()) return {};
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  },
};


/**
 * Triggers API endpoints
 */
export const triggersAPI = {
  /**
   * List triggers with pagination and filtering
   * @param {Object} params - Query parameters (optional)
   * @param {number} [params.page] - Page number for pagination (optional, default: 1)
   * @param {number} [params.per_page] - Number of triggers per page (optional, default: 20)
   * @param {string} [params.action] - Filter by action type (e.g., "EMAIL", "SMS", "HTTP") (optional)
   * @param {string} [params.event] - Filter by event type (optional)
   * @returns {Promise<Object>} List of triggers with pagination metadata
   */
  list: async (params = {}) => {
    const queryParams = new URLSearchParams();

    if (params.page) {
      queryParams.append("page", params.page);
    }
    if (params.per_page) {
      queryParams.append("per_page", params.per_page);
    }
    if (params.action) {
      queryParams.append("action", params.action);
    }
    if (params.event) {
      queryParams.append("event", params.event);
    }

    const queryString = queryParams.toString();
    const endpoint = `/triggers${queryString ? `?${queryString}` : ""}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Get trigger by ID
   * @param {number} triggerId - Trigger ID
   * @returns {Promise<Object>} Trigger data
   */
  get: async (triggerId) => {
    const endpoint = `/triggers/${triggerId}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Get triggers by rule ID
   * @param {number} ruleId - Rule ID
   * @returns {Promise<Object>} Triggers data for the rule
   */
  getByRuleId: async (ruleId) => {
    const endpoint = `/triggers/rule/${ruleId}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Update an existing trigger
   * @param {number} triggerId - Trigger ID
   * @param {Object} triggerData - Updated trigger data (partial update supported)
   * @returns {Promise<Object>} Updated trigger data
   */
  update: async (triggerId, triggerData) => {
    const endpoint = `/triggers/${triggerId}`;
    try {
      const response = await apiRequest(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(triggerData),
      });
      return response;
    } catch (error) {
      // Log detailed error information for 422 errors
      if (error.status === 422) {
        if (process.env.NODE_ENV === "development") {
          console.error("Validation Error (422):", {
            message: error.message,
            errors: error.errors,
            data: error.data,
            requestBody: triggerData,
          });
        }
      }
      throw error;
    }
  },

  /**
   * Create a new trigger
   * @param {Object} triggerData - Trigger data
   * @param {string} triggerData.name - Trigger name
   * @param {string} [triggerData.event] - Event type (e.g., "SECURITY_ALERT") when required by API
   * @param {string} triggerData.action - Action type (e.g., "EMAIL", "SMS", "WEBHOOK")
   * @param {string} [triggerData.notified_to] - Comma-separated display names (Notified to)
   * @param {Object} triggerData.config - Configuration object
   * @param {Array<string>} triggerData.config.recipients - Email addresses for EMAIL action
   * @param {Array<string>} triggerData.config.cc - CC email addresses (optional)
   * @param {Array<string>} triggerData.config.bcc - BCC email addresses (optional)
   * @param {string} triggerData.config.subject - Email subject (for EMAIL action)
   * @param {string} triggerData.config.message_body - Message body
   * @returns {Promise<Object>} Created trigger data
   */
  create: async (triggerData) => {
    const endpoint = `/triggers/`;
    try {
      const response = await apiRequest(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(triggerData),
      });
      return response;
    } catch (error) {
      // Log detailed error information for 422 errors
      if (error.status === 422) {
        if (process.env.NODE_ENV === "development") {
          console.error("Validation Error (422):", {
            message: error.message,
            errors: error.errors,
            data: error.data,
            requestBody: triggerData,
          });
        }
      }
      throw error;
    }
  },

  /**
   * Delete a trigger by ID
   * @param {number} triggerId - Trigger ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: async (triggerId) => {
    const endpoint = `/triggers/${triggerId}`;
    try {
      const response = await apiRequest(endpoint, { method: "DELETE" });
      return response;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error deleting trigger:", {
          status: error.status,
          message: error.message,
          errors: error.errors,
          data: error.data,
        });
      }
      throw error;
    }
  },
};


/**
 * Notifications API — catalog for trigger message_body template placeholders.
 */
export const notificationsAPI = {
  /**
   * List placeholder keys supported in EMAIL / TELEGRAM / SMS trigger message_body templates.
   * @returns {Promise<{ success?: boolean; message?: string; data?: { template_style?: string; placeholder_syntax?: string; applies_to?: string[]; placeholders?: Array<{ key: string; label: string; category: string; value_kind: string }> } }>}
   */
  getMessagePlaceholders: async () => {
    return getWithoutQueryString("/notifications/message-placeholders");
  },
};


/**
 * Alert Rules API endpoints
 */
export const alertRulesAPI = {
  /**
   * List all rules with optional filtering
   * @param {Object} params - Query parameters (optional)
   * @returns {Promise<Object>} List of rules
   */
  list: async (params = {}) => {
    const queryParams = new URLSearchParams();

    // Add any query parameters if provided
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const endpoint = `/rules${queryString ? `?${queryString}` : ""}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Get rule by ID
   * @param {number|string} ruleId - Rule ID
   * @returns {Promise<Object>} Rule data
   */
  get: async (ruleId) => {
    const endpoint = `/rules/${ruleId}`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Update an existing rule
   * @param {number|string} ruleId - Rule ID
   * @param {Object} ruleData - Updated rule data (partial update supported)
   * @param {string} [ruleData.alert_name] - Rule name
   * @param {string} [ruleData.alert_type] - Alert type
   * @returns {Promise<Object>} Updated rule data
   */
  update: async (ruleId, ruleData) => {
    const endpoint = `/rules/${ruleId}`;
    try {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "Updating rule:",
          ruleId,
          "with data:",
          JSON.stringify(ruleData, null, 2),
        );
      }
      const response = await apiRequest(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ruleData),
      });
      return response;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error updating rule:", {
          status: error.status,
          message: error.message,
          errors: error.errors,
          data: error.data,
          requestBody: ruleData,
        });
      }
      throw error;
    }
  },

  /**
   * Update a rule's favorite status
   * @param {number|string} ruleId - Rule ID
   * @param {boolean} isFav - Favorite status
   * @returns {Promise<Object>} Updated rule data
   */
  patchRuleFavorite: async (ruleId, isFav) => {
    const endpoint = `/rules/${ruleId}/favorite`;
    const response = await apiRequest(endpoint, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ is_fav: isFav }),
    });
    return response;
  },

  /**
   * Create a new alert rule
   * @param {Object} ruleData - Rule data
   * @param {string} ruleData.alert_name - Rule name
   * @param {string} ruleData.source_type - Source type (e.g., "camera")
   * @param {string} ruleData.alert_type - Alert type
   * @param {number} ruleData.source_id - Source ID
   * @param {number} [ruleData.roi_id] - ROI ID (optional)
   * @param {string} ruleData.rule_severity - Severity level (e.g., "high", "medium", "low")
   * @param {Object} ruleData.schedule - Schedule object with days, start_time, end_time
   * @param {number} ruleData.cooldown_interval - Cooldown interval in seconds
   * @param {Array<number>} ruleData.trigger_ids - Array of trigger IDs
   * @returns {Promise<Object>} Created rule data
   */
  create: async (ruleData) => {
    const endpoint = `/rules/`;
    try {
      if (process.env.NODE_ENV === "development") {
        console.log(
          "Creating rule with data:",
          JSON.stringify(ruleData, null, 2),
        );
      }
      const response = await apiRequest(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ruleData),
      });
      return response;
    } catch (error) {
      // Log detailed error information for all errors
      if (process.env.NODE_ENV === "development") {
        console.error("Error creating rule:", {
          status: error.status,
          message: error.message,
          errors: error.errors,
          data: error.data,
          requestBody: ruleData,
        });
      }

      if (error.status === 422) {
        if (process.env.NODE_ENV === "development") {
          console.error("Validation Error (422):", {
            message: error.message,
            errors: error.errors,
            data: error.data,
            requestBody: ruleData,
          });
        }
      } else if (error.status === 500) {
        if (process.env.NODE_ENV === "development") {
          console.error("Server Error (500):", {
            message: error.message,
            data: error.data,
            requestBody: ruleData,
          });
        }
      }
      throw error;
    }
  },

  /**
   * Delete a rule by ID
   * @param {number|string} ruleId - Rule ID
   * @returns {Promise<Object>} Deletion response
   */
  delete: async (ruleId) => {
    const endpoint = `/rules/${ruleId}`;
    try {
      if (process.env.NODE_ENV === "development") {
        console.log("Deleting rule:", ruleId);
      }
      const response = await apiRequest(endpoint, {
        method: "DELETE",
      });
      return response;
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error deleting rule:", {
          status: error.status,
          message: error.message,
          data: error.data,
        });
      }
      throw error;
    }
  },
};


/**
 * PUT notification-credentials expect a wrapped body:
 * `{ "credentials": { ... } }` (SMS, EMAIL, TELEGRAM).
 * @param {string} _serviceType
 * @param {Object} credentials
 * @returns {string}
 */
function serializeNotificationCredentialsBody(_serviceType, credentials) {
  const creds =
    credentials != null && typeof credentials === "object" && !Array.isArray(credentials)
      ? credentials
      : {};
  return JSON.stringify({ credentials: creds });
}

/**
 * Notification Credentials API endpoints
 */
export const notificationCredentialsAPI = {
  /**
   * Get all notification credentials for the logged-in user.
   * @returns {Promise<Object>} All credentials data
   */
  getAll: async () => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/notification-credentials/${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "GET",
    });
    return response;
  },

  /**
   * Get notification credentials for a service type
   * @param {string} serviceType - Service type (TELEGRAM, EMAIL, SMS)
   * @returns {Promise<Object>} Credentials data
   */
  get: async (serviceType) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/notification-credentials/${serviceType}${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "GET",
    });
    return response;
  },

  /**
   * Create or replace notification credentials for a service type (PUT upsert).
   * When a row already exists, omitted credential fields are left unchanged per API contract.
   * @param {string} serviceType - Service type (TELEGRAM, EMAIL, SMS)
   * @param {Object} credentials - Credentials data
   * @returns {Promise<Object>} Upserted credentials data
   */
  update: async (serviceType, credentials) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/notification-credentials/${serviceType}${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: serializeNotificationCredentialsBody(serviceType, credentials),
    });
    return response;
  },

  /**
   * Delete notification credentials for a service type
   * @param {string} serviceType - Service type (TELEGRAM, EMAIL, SMS)
   * @returns {Promise<Object>} Deletion response
   */
  delete: async (serviceType) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/notification-credentials/${serviceType}${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "DELETE",
    });
    return response;
  },
};


/**
 * Alert Rules Settings API endpoints
 */
export const alertRulesSettingsAPI = {
  /**
   * Get alert rules settings
   * @returns {Promise<Object>} Settings data
   */
  get: async () => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/alert-rules/settings${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, { method: "GET" });
    return response;
  },

  /**
   * Update alert rules settings
   * @param {Object} settingsData - Settings data to update
   * @returns {Promise<Object>} Updated settings data
   */
  update: async (settingsData) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    const queryString = queryParams.toString();
    const endpoint = `/alert-rules/settings${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(settingsData),
    });
    return response;
  },

  /**
   * Delete alert rules settings for a specific service type
   * @param {string} serviceType - Service type to delete (TELEGRAM, EMAIL, SMS)
   * @returns {Promise<Object>} Deletion response
   */
  delete: async (serviceType) => {
    const queryParams = new URLSearchParams();
    appendUserIdQueryParam(queryParams);
    if (serviceType) {
      queryParams.append("service_type", serviceType);
    }
    const queryString = queryParams.toString();
    const endpoint = `/alert-rules/settings${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await apiRequest(endpoint, {
      method: "DELETE",
    });
    return response;
  },
};
